// /redaktionen — namngiven avsändare bakom innehållet (E-E-A-T + GEO).
// AI-motorer och Google väger "vem står bakom siffrorna" tungt. Sidan renderas
// från src/content/editorial.ts så att byline, Person-schema och den här sidan
// alltid säger samma sak.

import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { breadcrumbs, person, speakable, aeoGeo } from "@/lib/seo/schema";
import { abs, SOCIAL_IMAGE_URL } from "@/lib/seo/constants";
import {
  EDITORIAL_PEOPLE,
  EDITORIAL_POLICY_PATH,
  REVIEW_CADENCE,
  authorPath,
} from "@/content/editorial";

const PATH = "/redaktionen";
const TITLE = "Redaktionen bakom vårt innehåll";
const DESCRIPTION =
  "Vilka som skriver, granskar och står bakom Fennys prisdata, guider och branschstudier — kompetensområden, granskningsrutiner och hur du når oss med rättelser.";

const PEOPLE = Object.entries(EDITORIAL_PEOPLE);

export const Route = createFileRoute("/redaktionen")({
  head: () => {
    const personLds = PEOPLE.map(([, p]) =>
      person({
        name: p.name,
        jobTitle: p.role,
        path: PATH,
        image: p.image,
        description: p.credentials,
        knowsAbout: p.knowsAbout,
        sameAs: [p.linkedin].filter(Boolean) as string[],
      }),
    );

    const crumbs = breadcrumbs([
      { name: "Hem", path: "/" },
      { name: "Redaktionen", path: PATH },
    ]);

    const speakableLd = { ...speakable([".lede"]), url: abs(PATH) };

    return {
      meta: [
        { title: "Redaktionen: vem skriver och granskar | Fenny" },
        { name: "description", content: DESCRIPTION },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:type", content: "website" },
        { property: "og:url", content: abs(PATH) },
        { property: "og:image", content: SOCIAL_IMAGE_URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        { name: "twitter:image", content: SOCIAL_IMAGE_URL },
      ],
      links: [{ rel: "canonical", href: abs(PATH) }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/redaktionen"})) },
        ...personLds.map((ld) => ({
          type: "application/ld+json",
          children: JSON.stringify(ld),
        })),
        { type: "application/ld+json", children: JSON.stringify(crumbs) },
        { type: "application/ld+json", children: JSON.stringify(speakableLd) },
      ],
    };
  },
  component: RedaktionenPage,
});

function RedaktionenPage() {
  return (
    <main className="bg-background text-foreground">
      <Section>
        <div className="mx-auto max-w-3xl space-y-6 py-12">
          <p className="text-sm uppercase tracking-wider text-muted-foreground">
            Om innehållet
          </p>
          <h1 className="text-4xl font-semibold sm:text-5xl">{TITLE}</h1>
          <p className="lede text-lg text-muted-foreground">{DESCRIPTION}</p>
          <div className="flex flex-wrap gap-3">
            <Button to={EDITORIAL_POLICY_PATH}>Läs redaktionell policy</Button>
            <Button to="/kontakt" variant="outline">
              Anmäl en rättelse
            </Button>
          </div>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-6 py-8">
          <h2 className="text-2xl font-semibold">Avsändare</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {PEOPLE.map(([key, p]) => (
              <article key={key} className="rounded-lg border border-border bg-card p-5">
                <h3 className="text-lg font-semibold">
                  <Link to={authorPath(key)} className="underline-offset-4 hover:underline">
                    {p.name}
                  </Link>
                </h3>
                <p className="text-sm text-muted-foreground">{p.role}</p>
                {p.credentials && (
                  <p className="mt-3 text-sm text-muted-foreground">{p.credentials}</p>
                )}
                {p.knowsAbout && p.knowsAbout.length > 0 && (
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {p.knowsAbout.map((k) => (
                      <li
                        key={k}
                        className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground"
                      >
                        {k}
                      </li>
                    ))}
                  </ul>
                )}
                {p.linkedin && (
                  <a
                    href={p.linkedin}
                    className="mt-3 inline-block text-sm underline"
                    rel="noopener"
                    target="_blank"
                  >
                    LinkedIn
                  </a>
                )}
              </article>
            ))}
          </div>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Hur ofta vi granskar om</h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-4 py-3">Innehållstyp</th>
                  <th className="px-4 py-3">Granskningsintervall</th>
                </tr>
              </thead>
              <tbody>
                {REVIEW_CADENCE.map((c) => (
                  <tr key={c.type} className="border-t border-border">
                    <td className="px-4 py-3 font-medium">{c.type}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.interval}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-muted-foreground">
            Våra öppna data ligger i{" "}
            <Link to="/branschstudier/prisindex-2026" className="underline">
              Prisindex 2026
            </Link>{" "}
            och{" "}
            <Link to="/branschstudier" className="underline">
              övriga branschstudier
            </Link>
            . Principerna för hur vi arbetar står i{" "}
            <Link to={EDITORIAL_POLICY_PATH} className="underline">
              den redaktionella policyn
            </Link>
            .
          </p>
        </div>
      </Section>
    </main>
  );
}
